import { create } from 'zustand';

/**
 * Events Store - Event-related state management
 * Handles event data, join/leave functionality, and participant management
 */
export const useEventsStore = create((set, get) => ({
  // State
  events: [],
  currentEvent: null,
  isLoading: false,
  error: null,

  // Mock event data for prototype
  mockEvents: {
    '1': {
      id: '1',
      title: 'Weekend Basketball Pickup',
      sport: 'basketball',
      sportLabel: 'Basketball',
      skillLevel: 'intermediate',
      skillLabel: 'Intermediate',
      isLive: false,
      date: 'Saturday, December 16, 2023',
      time: '2:00 PM - 4:00 PM',
      location: {
        name: 'Central Park Basketball Court',
        address: '123 Park Ave, New York, NY 10001',
        coordinates: { lat: 40.7829, lng: -73.9654 },
      },
      host: {
        id: '101',
        name: 'Michael Chen',
        avatar: null,
        rating: 4.9,
        eventsHosted: 23,
      },
      description: 'Join us for a friendly pickup basketball game! All skill levels welcome, but we focus on intermediate play. We\'ll split into teams based on who shows up. Bring water and your A-game! We usually play 5v5 with subs.\n\nWhat to bring:\n- Basketball shoes\n- Water bottle\n- Towel\n- Positive attitude!\n\nWe\'ll have extra basketballs available if you don\'t have one. Looking forward to seeing everyone on the court!',
      participants: {
        count: 8,
        max: 12,
        list: [
          { id: '1', name: 'Alex Johnson', avatar: null },
          { id: '2', name: 'Sarah Williams', avatar: null },
          { id: '3', name: 'Mike Brown', avatar: null },
          { id: '4', name: 'Emma Davis', avatar: null },
          { id: '5', name: 'Chris Lee', avatar: null },
          { id: '6', name: 'Jordan Taylor', avatar: null },
          { id: '7', name: 'Morgan Smith', avatar: null },
          { id: '8', name: 'Casey Martinez', avatar: null },
        ],
      },
      price: 0,
      amenities: ['Parking', 'Water Fountain', 'Benches', 'Lights', 'Restrooms'],
      isJoined: false,
      status: 'active',
    },
    '2': {
      id: '2',
      title: 'Evening Basketball Run',
      sport: 'basketball',
      sportLabel: 'Basketball',
      skillLevel: 'all-levels',
      skillLabel: 'All Levels',
      isLive: true,
      date: 'Sunday, December 17, 2023',
      time: '6:00 PM - 8:00 PM',
      location: {
        name: 'Downtown Community Court',
        address: '456 Main St, New York, NY 10002',
        coordinates: { lat: 40.7589, lng: -73.9851 },
      },
      host: {
        id: '102',
        name: 'Sarah Williams',
        avatar: null,
        rating: 4.7,
        eventsHosted: 15,
      },
      description: 'Casual evening basketball for all skill levels. Great way to end the weekend!',
      participants: {
        count: 6,
        max: 10,
        list: [
          { id: '9', name: 'David Kim', avatar: null },
          { id: '10', name: 'Lisa Chen', avatar: null },
          { id: '11', name: 'Tom Wilson', avatar: null },
          { id: '12', name: 'Rachel Green', avatar: null },
          { id: '13', name: 'James Brown', avatar: null },
          { id: '14', name: 'Amy White', avatar: null },
        ],
      },
      price: 5,
      amenities: ['Parking', 'Showers', 'Lockers', 'Equipment Rental'],
      isJoined: true,
      status: 'active',
    },
    '3': {
      id: '3',
      title: '3v3 Tournament',
      sport: 'basketball',
      sportLabel: 'Basketball',
      skillLevel: 'advanced',
      skillLabel: 'Advanced',
      isLive: false,
      date: 'Saturday, December 23, 2023',
      time: '10:00 AM - 4:00 PM',
      location: {
        name: 'Metro Sports Complex',
        address: '789 Sports Blvd, New York, NY 10003',
        coordinates: { lat: 40.7505, lng: -73.9934 },
      },
      host: {
        id: '103',
        name: 'Metro Sports League',
        avatar: null,
        rating: 5.0,
        eventsHosted: 150,
      },
      description: 'Competitive 3v3 basketball tournament with prizes!',
      participants: {
        count: 24,
        max: 24,
        list: Array.from({ length: 24 }, (_, i) => ({
          id: `p${i + 1}`,
          name: `Player ${i + 1}`,
          avatar: null,
        })),
      },
      price: 20,
      amenities: ['Parking', 'Showers', 'Lockers', 'Water Station', 'First Aid', 'Concessions'],
      isJoined: false,
      status: 'full',
    },
  },

  // Related events mock data
  relatedEvents: [
    { id: '4', title: 'Morning Yoga Flow', date: 'Sun, Dec 17', time: '8:00 AM', location: 'Sunrise Studio', sport: 'yoga', participants: 12, maxParticipants: 20 },
    { id: '5', title: 'Soccer Pickup', date: 'Sat, Dec 23', time: '3:00 PM', location: 'Riverside Field', sport: 'soccer', participants: 14, maxParticipants: 22 },
    { id: '6', title: 'Tennis Singles', date: 'Sun, Dec 24', time: '10:00 AM', location: 'City Tennis Club', sport: 'tennis', participants: 2, maxParticipants: 2 },
    { id: '7', title: 'Volleyball Beach', date: 'Sat, Dec 30', time: '2:00 PM', location: 'Sandy Beach', sport: 'volleyball', participants: 8, maxParticipants: 12 },
  ],

  // Actions
  fetchEvent: async (eventId) => {
    set({ isLoading: true, error: null });

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 500));

    const event = get().mockEvents[eventId];

    if (event) {
      set({
        currentEvent: event,
        isLoading: false,
      });
      return event;
    }

    set({
      error: 'Event not found',
      isLoading: false,
    });
    return null;
  },

  joinEvent: async (eventId) => {
    set({ isLoading: true });

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 600));

    const event = get().mockEvents[eventId];
    if (event && event.participants.count < event.participants.max) {
      event.isJoined = true;
      event.participants.count += 1;
      // Add current user to participants list
      event.participants.list.push({
        id: 'current-user',
        name: 'You',
        avatar: null,
      });

      set({
        currentEvent: { ...event },
        isLoading: false,
      });
      return { success: true, message: 'Successfully joined the event!' };
    }

    set({ isLoading: false });
    return { success: false, message: 'Could not join event. It may be full.' };
  },

  leaveEvent: async (eventId) => {
    set({ isLoading: true });

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 600));

    const event = get().mockEvents[eventId];
    if (event && event.isJoined) {
      event.isJoined = false;
      event.participants.count -= 1;
      // Remove current user from participants list
      event.participants.list = event.participants.list.filter(
        (p) => p.id !== 'current-user'
      );

      set({
        currentEvent: { ...event },
        isLoading: false,
      });
      return { success: true, message: 'You have left the event.' };
    }

    set({ isLoading: false });
    return { success: false, message: 'Could not leave event.' };
  },

  getRelatedEvents: (currentEventId, limit = 4) => {
    return get().relatedEvents
      .filter((e) => e.id !== currentEventId)
      .slice(0, limit);
  },

  clearCurrentEvent: () => {
    set({ currentEvent: null, error: null });
  },

  clearError: () => set({ error: null }),
}));

export default useEventsStore;
